import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';

import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import { KeyValue } from '@/main/models/common';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { filterCompanyTypeByEnums, getTranslateByKey, IConditionMessage, isEmpty, isNullUndefined } from '@/main/services/utils';
import { getCompanyAll } from '@/main/store/company';
import { getCompanyType, useCompanyTypeReducer } from '@/main/store/companyType';
import CompanyAllDropdown from '@/main/components/shared/companyAllDropdown/companyAllDropdown';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';
import { Controls } from '@/main/services/constants/constantsControl';
import { currencyToNumber } from '@/main/services/utilsNumber';

import { IDataPriceList } from '../sectionPriceList';
import InputTextPriceList from './inputTextPriceList/inputTextPriceList';
import * as S from './styles';

const INITIAL_FORM_STATE = {
  companyType: null as KeyValue | null,
  company: null as KeyValue | null,
  allCompany: true as boolean,
  priceLogisticCompany: '' as string | number,
  priceAutoDeliveryWithoutCost: '' as string | number,
  priceAutoDeliveryWithCost: '' as string | number,
  noPriceLogisticCompany: false as boolean,
  noPriceAutoDeliveryWithoutCost: false as boolean,
  noPriceAutoDeliveryWithCost: false as boolean,
  guidSupplierInventoryWarehousePrice: '' as string
};
type IPriceListForm = typeof INITIAL_FORM_STATE;

interface IFormPriceListProps {
  watcherCountry: KeyValue | null;
  dataPriceList: IDataPriceList[];
  idClientEdit: string;
  setDataPriceList: React.Dispatch<React.SetStateAction<IDataPriceList[]>>;
  setIdClientEdit: React.Dispatch<React.SetStateAction<string>>;
}

function FormPriceList({
  watcherCountry,
  dataPriceList,
  idClientEdit,
  setIdClientEdit,
  setDataPriceList
}: IFormPriceListProps) {
  const dispatch = useDispatch();
  const { companyType: companyTypeReducer } = useCompanyTypeReducer();
  const CONTROL_SHOW_COMPANY = useKogControlElementId(Controls.IdImportRegisterCompany);
  const methods = useForm<IPriceListForm>({
    defaultValues: INITIAL_FORM_STATE
  });
  const {
    control,
    reset,
    handleSubmit,
    resetField,
    setValue,
  } = methods;
  const isEdit = !isEmpty(idClientEdit);

  function validateForm(data: IPriceListForm) {
    const OBJ_VALIDATIONS: Record<string, IConditionMessage> = {
      is3NoPrice: {
        condition: data.noPriceLogisticCompany && data.noPriceAutoDeliveryWithCost && data.noPriceAutoDeliveryWithoutCost,
        message: getTranslateByKey('information.haveAtLeastOnePrice', true)
      }

    };

    const findedError = Object.entries(OBJ_VALIDATIONS).find(([_, { condition }]) => condition)

    if (findedError) {
      toast.error(findedError[1].message);
    }

    return !findedError;
  }

  async function callCompanyApiDomain(companyTypeSelected: KeyValue | null) {
    resetField('company');

    if (isNullUndefined(companyTypeSelected) || isNullUndefined(watcherCountry)) return;

    dispatch(
      getCompanyAll({
        guidCountry: String(watcherCountry.key),
        idCompanyType: String(companyTypeSelected.key),
        idStatusRegister: StatusRegisterEnum.Active
      })
    );
  }

  function onSubmit(data: IPriceListForm) {
    if (!validateForm(data)) return;

    const newDataPriceList: IDataPriceList = {
      idClient: uuidV4(),
      guidSupplierInventoryWarehousePrice: data.guidSupplierInventoryWarehousePrice,
      companyType: data.companyType as KeyValue,
      company: data.company as KeyValue,
      priceLogisticCompany: currencyToNumber(String(data.priceLogisticCompany)),
      priceAutoDeliveryWithoutCost: currencyToNumber(String(data.priceAutoDeliveryWithoutCost)),
      priceAutoDeliveryWithCost: currencyToNumber(String(data.priceAutoDeliveryWithCost)),
      isAllCompany: data.allCompany,
      isNoPriceLogisticCompany: data.noPriceLogisticCompany,
      isNoPriceAutoDeliveryWithoutCost: data.noPriceAutoDeliveryWithoutCost,
      isNoPriceAutoDeliveryWithCost: data.noPriceAutoDeliveryWithCost
    };

    reset();

    if (isEdit) {
      setDataPriceList(dataPriceList.map(price => price.idClient === idClientEdit ? newDataPriceList : price));
      setIdClientEdit('');
      return;
    }

    setDataPriceList(prev => [...prev, newDataPriceList]);
  }

  function loadEdit() {
    if (isEmpty(idClientEdit)) return;

    const priceList = dataPriceList.find(price => price.idClient === idClientEdit);
    if (isNullUndefined(priceList)) return;
    callCompanyApiDomain(priceList.companyType)

    setValue('companyType', priceList.companyType);
    setValue('company', priceList.company);
    setValue('priceLogisticCompany', isNullUndefined(priceList.priceLogisticCompany) ? '' : priceList.priceLogisticCompany);
    setValue('priceAutoDeliveryWithoutCost', isNullUndefined(priceList.priceAutoDeliveryWithoutCost) ? '' : priceList.priceAutoDeliveryWithoutCost);
    setValue('priceAutoDeliveryWithCost', isNullUndefined(priceList.priceAutoDeliveryWithCost) ? '' : priceList.priceAutoDeliveryWithCost);
    setValue('noPriceLogisticCompany', priceList.isNoPriceLogisticCompany);
    setValue('noPriceAutoDeliveryWithoutCost', priceList.isNoPriceAutoDeliveryWithoutCost);
    setValue('noPriceAutoDeliveryWithCost', priceList.isNoPriceAutoDeliveryWithCost);
    setValue('guidSupplierInventoryWarehousePrice', isNullUndefined(priceList.guidSupplierInventoryWarehousePrice) ? '' : priceList.guidSupplierInventoryWarehousePrice);
    setValue('allCompany', priceList.isAllCompany);

  }

  useEffect(() => {
    if (isNullUndefined(companyTypeReducer.data)) dispatch(getCompanyType());
  }, []);

  useEffect(() => {
    loadEdit()
  }, [idClientEdit]);

  useEffect(() => {
    if (CONTROL_SHOW_COMPANY) {
      setValue('allCompany', false);
    }
  }, [CONTROL_SHOW_COMPANY]);

  const companyTypesFilters = [
    CompanyTypeEnum.BodyShop,
    CompanyTypeEnum.Insurer,
  ];
  const optionsFilteredCompanyType = filterCompanyTypeByEnums(companyTypeReducer.data, companyTypesFilters);
  const ID_SCREEN = 'id-inventory-register-pricelist';

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <S.Container>
          <KogAutoCompleteControlled
            required
            id="id-inventory-register-pricelist-companyType"
            label={getTranslateByKey('information.companyTypeName')}
            control={control}
            options={optionsFilteredCompanyType}
            isLoading={companyTypeReducer.isLoading}
            nameCustom="companyType"
            rulesCustom={{ required: true }}
            setValueCustom={setValue}
            onChangeCustom={(value) => { callCompanyApiDomain(value) }}
          />

          {CONTROL_SHOW_COMPANY && (<CompanyAllDropdown idScreen={ID_SCREEN} />)}
          <S.AreaButton>
            <SecondaryButton
              id='id-inventory-register-pricelist-submit'
              type='submit'
            >
              {isEdit ? getTranslateByKey('information.editPrice') : getTranslateByKey('information.addNewPrice')}
            </SecondaryButton>
          </S.AreaButton>

          {!CONTROL_SHOW_COMPANY && <div />}

          <InputTextPriceList
            idScreen={ID_SCREEN}
            nameCheckbox='noPriceLogisticCompany'
            nameInput='priceLogisticCompany'
            label={getTranslateByKey('information.shippingPrice')}
          />
          <InputTextPriceList
            idScreen={ID_SCREEN}
            nameCheckbox='noPriceAutoDeliveryWithCost'
            nameInput='priceAutoDeliveryWithCost'
            label={getTranslateByKey('information.autoDeliveryWithCost')}
          />
          <InputTextPriceList
            idScreen={ID_SCREEN}
            nameCheckbox='noPriceAutoDeliveryWithoutCost'
            nameInput='priceAutoDeliveryWithoutCost'
            label={getTranslateByKey('information.autoDeliverywithoutPrice')}
          />

        </S.Container>
      </form>
    </FormProvider>

  )
}

export default FormPriceList